import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { get, post, patch, put } from '../lib/api'
import { useNavigate, useLocation } from 'react-router-dom'

const C = {
  bg: '#F8F9FA', white: '#FFFFFF', primary: '#4F46E5', primary2: '#6366F1',
  text: '#1E293B', textSub: '#475569', textMuted: '#9CA3AF',
  border: '#E5E7EB', success: '#10B981', danger: '#EF4444',
  amber: '#F59E0B', shadow: '0 1px 3px rgba(0,0,0,0.08)',
}

const emptyGoal = () => ({ title: '', description: '', kpi: '', weightage: '' })

const LS = { display: 'block', fontSize: 13, fontWeight: 600, color: '#1E293B', marginBottom: 6 }
const IS = { display: 'block', width: '100%', padding: '11px 14px', background: '#F8F9FA', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14, color: '#1E293B', fontFamily: 'Inter, system-ui', boxSizing: 'border-box', outline: 'none' }

export default function PDRCreate() {
  const { profile } = useAuth()
  const navigate    = useNavigate()
  const location    = useLocation()

  const draft = location.state?.draft || null

  const [step, setStep]       = useState(1)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState('')
  const [savedMsg, setSavedMsg] = useState('')
  const [draftId, setDraftId] = useState(draft?.id || null)

  const today = new Date().toISOString().split('T')[0]

  const [pdrForm, setPdrForm] = useState({
    title:       draft?.title       || '',
    review_type: draft?.review_type || 'quarterly',
    start_date:  draft?.start_date  || '',
    end_date:    draft?.end_date    || '',
  })

  const [goals, setGoals] = useState(
    draft?.pdr_goals?.length
      ? draft.pdr_goals.sort((a,b) => a.goal_number - b.goal_number).map(g => ({
          title: g.title || '', description: g.description || '', kpi: g.kpi || '', weightage: String(g.weightage || ''),
        }))
      : [emptyGoal(), emptyGoal(), emptyGoal()]
  )

  const totalWeightage = goals.reduce((s, g) => s + (parseInt(g.weightage) || 0), 0)

  function addGoal()           { if (goals.length < 5) setGoals([...goals, emptyGoal()]) }
  function removeGoal(i)       { if (goals.length > 1) setGoals(goals.filter((_,idx) => idx !== i)) }
  function updateGoal(i, f, v) { const u=[...goals]; u[i]={...u[i],[f]:v}; setGoals(u) }

  async function saveDraft() {
    if (!pdrForm.title.trim()) return setError('Please enter a PDR title to save as draft')
    setSaving(true); setError(''); setSavedMsg('')

    const payload = {
      title: pdrForm.title, review_type: pdrForm.review_type,
      start_date: pdrForm.start_date || null,
      end_date:   pdrForm.end_date   || null,
      status: 'draft',
    }

    let pId = draftId
    try {
      if (pId) {
        await patch(`/pdrs/${pId}`, payload)
      } else {
        const { pdr } = await post('/pdrs', payload)
        pId = pdr.id
        setDraftId(pId)
      }

      const filled = goals.filter(g => g.title.trim())
      if (filled.length > 0) {
        await put(`/pdrs/${pId}/goals`, {
          goals: filled.map((g, i) => ({
            goal_number: i+1, title: g.title, description: g.description,
            kpi: g.kpi, weightage: parseInt(g.weightage) || 0,
          })),
        })
      }
    } catch (e) { setError(e.message); setSaving(false); return }

    setSaving(false)
    setSavedMsg('✅ Draft saved!')
    setTimeout(() => setSavedMsg(''), 3000)
  }

  async function handleSubmit() {
    setError('')
    if (!pdrForm.title || !pdrForm.start_date || !pdrForm.end_date)
      return setError('Please fill in all PDR details')
    if (pdrForm.start_date < today)
      return setError('Start date cannot be in the past')
    if (pdrForm.end_date <= pdrForm.start_date)
      return setError('End date must be after start date')
    const filledGoals = goals.filter(g => g.title.trim())
    if (filledGoals.length < 1) return setError('Please add at least 1 goal')
    if (totalWeightage !== 100) return setError(`Total weightage must equal 100%. Currently ${totalWeightage}%`)

    // Date overlap check
    let existing = []
    try {
      const res = await get('/pdrs')
      existing = (res.pdrs || []).filter(p => ['active', 'pending_approval'].includes(p.status))
    } catch { /* ignore */ }
    const overlaps = existing.filter(p => {
      if (draftId && p.id === draftId) return false
      return pdrForm.start_date <= p.end_date && pdrForm.end_date >= p.start_date
    })
    if (overlaps.length > 0) {
      const p = overlaps[0]
      return setError(`You already have a PDR "${p.title}" covering this period (${p.start_date} → ${p.end_date}). Please choose non-overlapping dates.`)
    }

    setLoading(true)
    const payload = {
      title: pdrForm.title, review_type: pdrForm.review_type,
      start_date: pdrForm.start_date, end_date: pdrForm.end_date,
      status: 'pending_approval',
    }

    let pId = draftId
    try {
      if (pId) {
        await patch(`/pdrs/${pId}`, payload)
      } else {
        const { pdr } = await post('/pdrs', payload)
        pId = pdr.id
      }
      await put(`/pdrs/${pId}/goals`, {
        goals: filledGoals.map((g, i) => ({
          goal_number: i+1, title: g.title, description: g.description,
          kpi: g.kpi, weightage: parseInt(g.weightage) || 0,
        })),
      })
    } catch (e) { setError(e.message); setLoading(false); return }
    setLoading(false)
    navigate('/dashboard')
  }

  function goNext() {
    if (step === 1) {
      if (!pdrForm.title || !pdrForm.start_date || !pdrForm.end_date) return setError('Please fill in all PDR details')
      if (pdrForm.start_date < today) return setError('Start date cannot be in the past')
      if (pdrForm.end_date <= pdrForm.start_date) return setError('End date must be after start date')
      setError(''); setStep(2)
    } else if (step === 2) {
      const filled = goals.filter(g => g.title.trim())
      if (filled.length < 1) return setError('Add at least 1 goal')
      if (totalWeightage !== 100) return setError(`Total weightage must be 100%. Currently ${totalWeightage}%`)
      setError(''); setStep(3)
    }
  }

  const DraftBtn = () => (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      {savedMsg && <span style={{ fontSize:12, color:C.success, fontWeight:600 }}>{savedMsg}</span>}
      <button onClick={saveDraft} disabled={saving}
        style={{ padding:'10px 18px', background:'#F1F5F9', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, fontWeight:600, color:'#475569', cursor:saving?'not-allowed':'pointer', fontFamily:'Inter,system-ui' }}>
        {saving ? 'Saving…' : '💾 Save Draft'}
      </button>
    </div>
  )

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:"'Inter', system-ui" }}>

      {/* Header */}
      <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, boxShadow:C.shadow, padding:'0 2rem', height:60, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={()=>navigate('/dashboard')} style={{ background:'none', border:'none', cursor:'pointer', fontSize:14, color:C.textSub }}>← Back</button>
          <div style={{ width:1, height:20, background:C.border }} />
          <span style={{ fontSize:15, fontWeight:600, color:C.text }}>{draft ? '✏️ Edit Draft PDR' : 'New PDR Cycle'}</span>
          {draftId && <span style={{ fontSize:11, fontWeight:600, background:'#F1F5F9', color:'#64748B', padding:'2px 8px', borderRadius:99 }}>Draft saved</span>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {['Cycle Details','Set Goals','Review & Submit'].map((s,i) => (
            <div key={s} style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <div style={{ width:24, height:24, borderRadius:'50%', background:step>i+1?C.success:step===i+1?C.primary:`${C.primary}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:step>=i+1?'#fff':C.textMuted }}>
                  {step>i+1?'✓':i+1}
                </div>
                <span style={{ fontSize:12, fontWeight:500, color:step===i+1?C.text:C.textMuted }}>{s}</span>
              </div>
              {i<2 && <div style={{ width:32, height:1, background:C.border }} />}
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth:1040, margin:'2rem auto', padding:'0 2rem' }}>

        {/* STEP 1 */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom:'2rem' }}>
              <h1 style={{ fontSize:24, fontWeight:800, color:C.text, margin:'0 0 6px', letterSpacing:'-0.5px' }}>PDR Cycle Details</h1>
              <p style={{ fontSize:15, color:C.textSub, margin:0 }}>Set up your performance development review cycle</p>
            </div>

            <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:14, padding:'2rem', boxShadow:C.shadow, marginBottom:'1.5rem' }}>
              <div style={{ marginBottom:'1.5rem' }}>
                <label style={LS}>PDR Title *</label>
                <input value={pdrForm.title} onChange={e=>setPdrForm({...pdrForm,title:e.target.value})}
                  placeholder="e.g. Q1 2026 Performance Review" style={IS} />
              </div>

              <div style={{ marginBottom:'1.5rem' }}>
                <label style={LS}>Review Type *</label>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
                  {[{value:'monthly',label:'Monthly',sub:'1 month'},{value:'quarterly',label:'Quarterly',sub:'3 months'},{value:'half-yearly',label:'Half-Yearly',sub:'6 months'},{value:'yearly',label:'Yearly',sub:'12 months'}].map(rt => (
                    <div key={rt.value} onClick={()=>setPdrForm({...pdrForm,review_type:rt.value})}
                      style={{ padding:'12px', borderRadius:10, border:`2px solid ${pdrForm.review_type===rt.value?C.primary:C.border}`, background:pdrForm.review_type===rt.value?`${C.primary}08`:C.white, cursor:'pointer', textAlign:'center' }}>
                      <div style={{ fontSize:14, fontWeight:600, color:pdrForm.review_type===rt.value?C.primary:C.text }}>{rt.label}</div>
                      <div style={{ fontSize:12, color:C.textMuted, marginTop:2 }}>{rt.sub}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div>
                  <label style={LS}>Start Date *</label>
                  <input type="date" value={pdrForm.start_date} min={today}
                    onChange={e=>setPdrForm({...pdrForm,start_date:e.target.value})} style={IS} />
                  <div style={{ fontSize:11, color:C.textMuted, marginTop:3 }}>Cannot be in the past</div>
                </div>
                <div>
                  <label style={LS}>End Date *</label>
                  <input type="date" value={pdrForm.end_date}
                    min={pdrForm.start_date || today}
                    onChange={e=>setPdrForm({...pdrForm,end_date:e.target.value})} style={IS} />
                </div>
              </div>
            </div>

            <div style={{ background:`${C.primary}08`, border:`1px solid ${C.primary}20`, borderRadius:14, padding:'1.25rem 1.5rem', display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ width:44, height:44, borderRadius:'50%', background:`linear-gradient(135deg,${C.primary},${C.primary2})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:'#fff', flexShrink:0 }}>{profile?.full_name?.charAt(0)}</div>
              <div>
                <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{profile?.full_name}</div>
                <div style={{ fontSize:13, color:C.textSub }}>{profile?.email}</div>
              </div>
              <div style={{ marginLeft:'auto', textAlign:'right' }}>
                <div style={{ fontSize:12, color:C.textMuted }}>Organization</div>
                <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{profile?.organizations?.name}</div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'2rem' }}>
              <div>
                <h1 style={{ fontSize:24, fontWeight:800, color:C.text, margin:'0 0 6px', letterSpacing:'-0.5px' }}>Set Your Goals</h1>
                <p style={{ fontSize:15, color:C.textSub, margin:0 }}>Add up to 5 goals. Total weightage must equal 100%.</p>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:28, fontWeight:900, color:totalWeightage===100?C.success:totalWeightage>100?C.danger:C.amber }}>{totalWeightage}%</div>
                <div style={{ fontSize:12, color:C.textMuted }}>of 100% allocated</div>
                <div style={{ marginTop:6, width:120, height:6, background:C.border, borderRadius:99, overflow:'hidden' }}>
                  <div style={{ width:`${Math.min(totalWeightage,100)}%`, height:'100%', background:totalWeightage===100?C.success:totalWeightage>100?C.danger:C.amber, borderRadius:99 }} />
                </div>
              </div>
            </div>

            {totalWeightage !== 100 && (
              <div style={{ background:totalWeightage>100?'#FEF2F2':'#FFFBEB', border:`1px solid ${totalWeightage>100?'#FECACA':'#FDE68A'}`, borderRadius:10, padding:'10px 14px', fontSize:13, color:totalWeightage>100?C.danger:C.amber, marginBottom:'1.25rem' }}>
                {totalWeightage>100?`⚠️ Over by ${totalWeightage-100}%`:`ℹ️ ${100-totalWeightage}% remaining`}
              </div>
            )}
            {totalWeightage === 100 && (
              <div style={{ background:'#ECFDF5', border:'1px solid #A7F3D0', borderRadius:10, padding:'10px 14px', fontSize:13, color:C.success, marginBottom:'1.25rem' }}>✅ Perfect — all 100% allocated</div>
            )}

            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {goals.map((goal, i) => (
                <div key={i} style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:14, overflow:'hidden', boxShadow:C.shadow }}>
                  <div style={{ background:`linear-gradient(135deg,${C.primary}08,${C.primary2}05)`, padding:'12px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:28, height:28, borderRadius:'50%', background:`linear-gradient(135deg,${C.primary},${C.primary2})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, color:'#fff' }}>{i+1}</div>
                      <span style={{ fontSize:14, fontWeight:700, color:C.text }}>{goal.title||`Goal ${i+1}`}</span>
                      {goal.weightage && <span style={{ fontSize:12, fontWeight:600, background:`${C.primary}14`, color:C.primary, padding:'2px 8px', borderRadius:99 }}>{goal.weightage}%</span>}
                    </div>
                    {goals.length > 1 && <button onClick={()=>removeGoal(i)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:C.textMuted }}>×</button>}
                  </div>
                  <div style={{ padding:'1.25rem 1.5rem' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 120px', gap:14, marginBottom:'1rem' }}>
                      <div>
                        <label style={LS}>Goal Title *</label>
                        <input value={goal.title} onChange={e=>updateGoal(i,'title',e.target.value)} placeholder="e.g. Launch Q3 marketing campaign" style={IS} />
                      </div>
                      <div>
                        <label style={LS}>Weightage % *</label>
                        <input type="text" inputMode="numeric" pattern="[0-9]*"
                          value={goal.weightage}
                          onChange={e => {
                            const v = e.target.value.replace(/[^0-9]/g,'')
                            if (v === '' || (parseInt(v) >= 1 && parseInt(v) <= 100)) updateGoal(i,'weightage',v)
                          }}
                          placeholder="30" style={{ ...IS, textAlign:'center', fontWeight:700, fontSize:16 }} />
                      </div>
                    </div>
                    <div style={{ marginBottom:'1rem' }}>
                      <label style={LS}>How will you achieve this goal?</label>
                      <textarea value={goal.description} onChange={e=>updateGoal(i,'description',e.target.value)} placeholder="Describe your plan and approach…" rows={6} style={{ ...IS, resize:'vertical', minHeight:150, lineHeight:1.6 }} />
                    </div>
                    <div>
                      <label style={LS}>KPI / Success Criteria</label>
                      <input value={goal.kpi} onChange={e=>updateGoal(i,'kpi',e.target.value)} placeholder="e.g. Achieve 5,000 followers by Jun 30" style={IS} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {goals.length < 5 && (
              <button onClick={addGoal} style={{ width:'100%', marginTop:12, padding:'14px', background:C.white, border:`2px dashed ${C.border}`, borderRadius:14, fontSize:14, fontWeight:600, color:C.textSub, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                <span style={{ fontSize:20 }}>+</span> Add another goal ({goals.length}/5)
              </button>
            )}
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div>
            <div style={{ marginBottom:'2rem' }}>
              <h1 style={{ fontSize:24, fontWeight:800, color:C.text, margin:'0 0 6px', letterSpacing:'-0.5px' }}>Review & Submit</h1>
              <p style={{ fontSize:15, color:C.textSub, margin:0 }}>Review your PDR before submitting to your manager for approval</p>
            </div>

            <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:14, padding:'1.5rem', boxShadow:C.shadow, marginBottom:'1.5rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.25rem', paddingBottom:'1.25rem', borderBottom:`1px solid ${C.border}` }}>
                <div>
                  <h2 style={{ fontSize:18, fontWeight:700, color:C.text, margin:'0 0 4px' }}>{pdrForm.title}</h2>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <span style={{ fontSize:12, fontWeight:600, background:`${C.primary}14`, color:C.primary, padding:'3px 10px', borderRadius:99, textTransform:'capitalize' }}>{pdrForm.review_type}</span>
                    <span style={{ fontSize:12, color:C.textMuted }}>{pdrForm.start_date} → {pdrForm.end_date}</span>
                  </div>
                </div>
                <span style={{ fontSize:12, fontWeight:600, background:'#FFFBEB', color:C.amber, padding:'4px 12px', borderRadius:99, border:'1px solid #FDE68A' }}>Pending Approval</span>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'30px 1fr 200px 80px', gap:12, padding:'8px 12px', background:C.bg, borderRadius:8, marginBottom:8 }}>
                {['#','Goal','KPI','Weight'].map(h=><div key={h} style={{ fontSize:11, fontWeight:700, color:C.textMuted, textTransform:'uppercase' }}>{h}</div>)}
              </div>
              {goals.filter(g=>g.title).map((g,i)=>(
                <div key={i} style={{ display:'grid', gridTemplateColumns:'30px 1fr 200px 80px', gap:12, padding:'10px 12px', borderBottom:`1px solid ${C.border}`, alignItems:'center' }}>
                  <div style={{ width:22, height:22, borderRadius:'50%', background:`${C.primary}14`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:C.primary }}>{i+1}</div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{g.title}</div>
                    {g.description && <div style={{ fontSize:12, color:C.textMuted, marginTop:2 }}>{g.description.slice(0,60)}{g.description.length>60?'…':''}</div>}
                  </div>
                  <div style={{ fontSize:12, color:C.textSub }}>{g.kpi||'—'}</div>
                  <div style={{ textAlign:'right' }}><span style={{ fontSize:15, fontWeight:800, color:C.primary }}>{g.weightage}%</span></div>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'flex-end', padding:'10px 12px' }}>
                <span style={{ fontSize:14, fontWeight:700, color:totalWeightage===100?C.success:C.danger }}>Total: {totalWeightage}%</span>
              </div>
            </div>

            <div style={{ background:`${C.primary}06`, border:`1px solid ${C.primary}20`, borderRadius:14, padding:'1.25rem 1.5rem', marginBottom:'1.5rem' }}>
              <h3 style={{ fontSize:14, fontWeight:700, color:C.text, margin:'0 0 8px' }}>📋 Declaration</h3>
              <p style={{ fontSize:13, color:C.textSub, margin:0, lineHeight:1.7 }}>I confirm that the goals and weightages listed above are accurate and reflect my planned performance targets for this review cycle. I understand this PDR will be submitted to my manager for approval before becoming active.</p>
            </div>

            {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'10px 14px', fontSize:13, color:C.danger, marginBottom:'1rem' }}>⚠️ {error}</div>}
          </div>
        )}

        {/* Nav */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'2rem', paddingTop:'1.5rem', borderTop:`1px solid ${C.border}` }}>
          <button onClick={()=>step>1?setStep(step-1):navigate('/dashboard')}
            style={{ padding:'11px 24px', background:C.white, border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, fontWeight:500, color:C.textSub, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
            {step===1?'← Cancel':'← Back'}
          </button>

          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <DraftBtn />
            {step < 3 ? (
              <button onClick={goNext}
                style={{ padding:'11px 28px', background:`linear-gradient(135deg,${C.primary},${C.primary2})`, border:'none', borderRadius:8, fontSize:14, fontWeight:600, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui', boxShadow:'0 2px 8px rgba(79,70,229,0.3)' }}>
                Continue →
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading||totalWeightage!==100}
                style={{ padding:'11px 28px', background:loading||totalWeightage!==100?C.border:`linear-gradient(135deg,${C.primary},${C.primary2})`, border:'none', borderRadius:8, fontSize:14, fontWeight:600, color:loading||totalWeightage!==100?C.textMuted:'#fff', cursor:loading||totalWeightage!==100?'not-allowed':'pointer', fontFamily:'Inter,system-ui' }}>
                {loading?'Submitting…':'🚀 Submit PDR for Approval'}
              </button>
            )}
          </div>
        </div>

        {error && step < 3 && (
          <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'10px 14px', fontSize:13, color:C.danger, marginTop:'1rem' }}>⚠️ {error}</div>
        )}
      </div>
    </div>
  )
}
